import React, { Dispatch, FC, SetStateAction, useEffect, useRef } from "react";
import styled from "styled-components";
import { ReactComponent as Logo } from "@assets/images/logo.svg";
import gsap, { Circ } from "gsap";
import { AnimatePresence, motion } from "framer-motion";
import { easeInOutCirc } from "@theme/easing";
import { breakpoints } from "@theme/breakpoints";
import { useLocation } from "react-use";

const StyledWrapper = styled(motion.div)`
  display: flex;
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  height: 100vh;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.background};
  z-index: 99999999;
  box-shadow: 0 0 40px -10px rgba(0, 0, 0, 0.2);
`;

const StyledLogoWrapper = styled.div`
  width: 130px;

  ${breakpoints.phoneMax`
    width: 100px;
  `}
`;

const StyledLogo = styled(Logo)`
  width: 100%;
`;

export const Loader: FC<Props> = ({
  isLoading,
  setLoaderExited,
  isLoaderExited,
  setLoading,
}) => {
  const logo = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();

  useEffect(() => {
    if (isLoaderExited || !logo.current) return;

    const tl = gsap.timeline();

    const letters = logo.current.querySelectorAll(".letter");

    gsap.set(letters, {
      opacity: 0,
    });

    tl.to(logo.current, {
      scale: 1.1,
      ease: Circ.easeInOut,
      duration: 1.2,
      yoyo: true,
      repeat: -1,
    }).to(letters, {
      opacity: 1,
      stagger: {
        each: 0.1,
      },
      duration: 0.4,
      repeat: -1,
      delay: -1.2,
      ease: Circ.easeInOut,
      yoyo: true,
    });
  }, [logo.current, isLoaderExited]);

  useEffect(() => {
    if (pathname !== "/") {
      setLoaderExited(true);
      setLoading(false);
    }
  }, []);

  if (pathname !== "/") {
    return <></>;
  }

  return (
    <AnimatePresence
      onExitComplete={() => setLoaderExited(true)}
      initial={true}
    >
      {isLoading && (
        <StyledWrapper
          exit={{ y: "100%" }}
          transition={{ duration: 1.5, ease: easeInOutCirc }}
        >
          <motion.div
            exit={{ scale: 0.6, opacity: 0 }}
            transition={{ duration: 0.9, ease: [0.85, 0, 0.15, 1] }}
          >
            <StyledLogoWrapper ref={logo}>
              <StyledLogo />
            </StyledLogoWrapper>
          </motion.div>
        </StyledWrapper>
      )}
    </AnimatePresence>
  );
};

interface Props {
  isLoading: boolean;
  isLoaderExited: boolean;
  setLoaderExited: Dispatch<SetStateAction<boolean>>;
  setLoading: Dispatch<SetStateAction<boolean>>;
}
